import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'fate-room-cookie-consent-v1';
const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const CookieConsentContext = createContext({
  consentLoaded: false,
  hasDecision: false,
  preferences: false,
  analytics: false,
  diagnostics: false,
  savePreferences: () => undefined,
  acceptAll: () => undefined,
  essentialOnly: () => undefined,
  openSettings: () => undefined,
  closeSettings: () => undefined,
  isSettingsOpen: false,
});

const readStoredConsent = () => {
  if (typeof window === 'undefined') return null;

  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (!saved?.updatedAt || Date.now() - new Date(saved.updatedAt).getTime() > CONSENT_MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return saved;
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(undefined);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    setConsent(readStoredConsent());
  }, []);

  const savePreferences = useCallback((choices) => {
    const next = {
      version: 1,
      necessary: true,
      preferences: Boolean(choices.preferences),
      analytics: Boolean(choices.analytics),
      diagnostics: Boolean(choices.diagnostics),
      updatedAt: new Date().toISOString(),
    };

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // The site remains usable if browser storage is unavailable.
    }

    setConsent(next);
    setIsSettingsOpen(false);
  }, []);

  const value = useMemo(() => ({
    consentLoaded: consent !== undefined,
    hasDecision: Boolean(consent),
    preferences: Boolean(consent?.preferences),
    analytics: Boolean(consent?.analytics),
    diagnostics: Boolean(consent?.diagnostics),
    isSettingsOpen,
    savePreferences,
    acceptAll: () => savePreferences({ preferences: true, analytics: true, diagnostics: true }),
    essentialOnly: () => savePreferences({ preferences: false, analytics: false, diagnostics: false }),
    openSettings: () => setIsSettingsOpen(true),
    closeSettings: () => setIsSettingsOpen(false),
  }), [consent, isSettingsOpen, savePreferences]);

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = () => useContext(CookieConsentContext);
